import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2, ShieldOff } from "lucide-react";
import api from "../lib/api";
import { FormShell } from "../components/RgpdForm";
import { getDict } from "../i18n";
import { getEmbedOrigin, hostAllowed } from "../lib/embed";

export default function PublicForm({ embed = false }) {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(false);
  const [lang, setLang] = useState("fr");
  const urlLang = params.get("lang");

  useEffect(() => {
    api
      .get(`/public/config/${token}`)
      .then((r) => {
        setConfig(r.data);
        const defLang = r.data.branding?.default_language || "fr";
        setLang(["fr", "en"].includes(urlLang) ? urlLang : defLang);
      })
      .catch(() => setError(true));
  }, [token, urlLang]);

  if (error) {
    const t = getDict(lang);
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div
          className="max-w-md rounded-xl border border-amber-200 bg-amber-50 p-6 text-center"
          role="alert"
          data-testid="form-unavailable"
        >
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" aria-hidden />
          <h1 className="mt-3 font-display text-lg font-semibold text-amber-900">{t.unavailableTitle}</h1>
          <p className="mt-1.5 text-sm text-amber-800">{t.unavailableText}</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" aria-label="Chargement" />
      </div>
    );
  }

  const integrationMode = embed ? (params.get("via") === "loader" ? "loader" : "iframe") : "full_page";
  const embedOrigin = embed ? getEmbedOrigin() : "";

  // Domain allowlist: block embedding from non-authorised parent sites.
  if (embed && !hostAllowed(config.allowed_domains, embedOrigin)) {
    const t = getDict(lang);
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div
          className="max-w-md rounded-xl border border-red-200 bg-red-50 p-6 text-center"
          role="alert"
          data-testid="form-domain-blocked"
        >
          <ShieldOff className="mx-auto h-8 w-8 text-red-600" aria-hidden />
          <h1 className="mt-3 font-display text-lg font-semibold text-red-900">{t.domainBlockedTitle}</h1>
          <p className="mt-1.5 text-sm text-red-800">{t.domainBlockedText}</p>
        </div>
      </div>
    );
  }

  return (
    <FormShell
      config={config}
      lang={lang}
      onLangChange={setLang}
      embed={embed}
      integrationMode={integrationMode}
      embedOrigin={embedOrigin}
    />
  );
}
